import { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { hrms } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import Card from '../../components/common/Card';
import Table from '../../components/common/Table';
import Badge from '../../components/common/Badge';
import Button from '../../components/common/Button';
import Modal from '../../components/common/Modal';
import { theme } from '../../theme';

function MyLeave() {
  const { user } = useAuth();
  const location = useLocation();
  const isManager = user?.is_team_lead || user?.role === 'super_admin';
  const [activeTab, setActiveTab] = useState(location.search.includes('tab=team') ? 'Team Requests' : 'My Leaves');
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [balances, setBalances] = useState([]);
  const [myRequests, setMyRequests] = useState([]);
  const [teamRequests, setTeamRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [applyModal, setApplyModal] = useState(false);
  const [form, setForm] = useState({ leave_type_id: '', start_date: '', end_date: '', reason: '', is_half_day: false, half_day_period: 'first_half' });
  const [rejectModal, setRejectModal] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [applyError, setApplyError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [lt, lb, mr] = await Promise.allSettled([hrms.getLeaveTypes(), hrms.getMyLeaveBalances(), hrms.getMyLeaveRequests()]);
      if (lt.status === 'fulfilled') setLeaveTypes(lt.value.data || []);
      if (lb.status === 'fulfilled') setBalances(lb.value.data || []);
      if (mr.status === 'fulfilled') { const d = mr.value.data; setMyRequests(Array.isArray(d) ? d : d.items || []); }
    } catch { } finally { setLoading(false); }
  }, []);

  const loadTeam = useCallback(async () => {
    try {
      const r = await hrms.getTeamLeaveRequests();
      const d = r.data;
      setTeamRequests(Array.isArray(d) ? d : d.items || []);
    } catch { setTeamRequests([]); }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (isManager && activeTab === 'Team Requests') loadTeam(); }, [isManager, activeTab, loadTeam]);

  const closeApplyModal = () => {
    setApplyModal(false);
    setApplyError('');
    setForm({ leave_type_id: '', start_date: '', end_date: '', reason: '', is_half_day: false, half_day_period: 'first_half' });
  };

  const handleApply = async () => {
    if (!form.leave_type_id || !form.start_date || !form.reason) {
      setApplyError('Please fill all required fields.'); return;
    }
    if (!form.is_half_day && !form.end_date) {
      setApplyError('Please select an end date.'); return;
    }
    setApplyError('');
    setSaving(true);
    try {
      const payload = {
        leave_type_id: form.leave_type_id,
        from_date: form.start_date,
        to_date: form.is_half_day ? form.start_date : form.end_date,
        reason: form.reason,
        is_half_day: form.is_half_day,
        half_day_period: form.is_half_day ? form.half_day_period : null,
      };
      await hrms.createLeaveRequest(payload);
      closeApplyModal();
      load();
    } catch (err) {
      setApplyError(err?.response?.data?.detail || 'Failed to submit leave request.');
    } finally { setSaving(false); }
  };

  const handleCancel = async (id) => {
    if (!window.confirm('Cancel this leave request?')) return;
    try {
      await hrms.cancelLeaveRequest(id); load();
    } catch { alert('Failed to cancel.'); }
  };

  const handleApprove = async (id) => {
    try { await hrms.approveLeaveRequest(id); loadTeam(); } catch { alert('Failed.'); }
  };

  const handleReject = async () => {
    setSaving(true);
    try {
      await hrms.rejectLeaveRequest(rejectModal, rejectReason);
      setRejectModal(null); setRejectReason(''); loadTeam();
    } catch { alert('Failed.'); } finally { setSaving(false); }
  };

  const myCols = [
    { header: 'Leave Type', key: 'leave_type_name', render: (v, row) => v || row.leave_type?.name || '—' },
    { header: 'From', key: 'from_date', render: (v, row) => (v || row.start_date) ? new Date(v || row.start_date).toLocaleDateString('en-IN') : '—' },
    { header: 'To',   key: 'to_date',   render: (v, row) => (v || row.end_date)   ? new Date(v || row.end_date).toLocaleDateString('en-IN')   : '—' },
    { header: 'Days', key: 'days', render: (v, row) => row.is_half_day ? `0.5 (${(row.half_day_period || 'first_half').replace('_', ' ')})` : (v || '—') },
    { header: 'Reason', key: 'reason', render: (v) => v ? <span style={{ whiteSpace: 'normal', wordBreak: 'break-word', display: 'block', minWidth: 120 }}>{v}</span> : '—' },
    { header: 'Status', key: 'status', render: (v, row) => <Badge status={v} byName={row.approved_by_name || row.pending_approver_name} /> },
    {
      header: 'Actions', key: 'id', align: 'right',
      render: (id, row) => row.status === 'pending' ? (
        <Button size="sm" variant="danger" onClick={() => handleCancel(id)}>Cancel</Button>
      ) : null,
    },
  ];

  const teamCols = [
    { header: 'Employee', key: 'employee_name', render: (v, row) => v || row.employee?.full_name || '—' },
    { header: 'Leave Type', key: 'leave_type_name', render: (v, row) => v || row.leave_type?.name || '—' },
    { header: 'From', key: 'from_date', render: (v, row) => (v || row.start_date) ? new Date(v || row.start_date).toLocaleDateString('en-IN') : '—' },
    { header: 'To',   key: 'to_date',   render: (v, row) => (v || row.end_date)   ? new Date(v || row.end_date).toLocaleDateString('en-IN')   : '—' },
    { header: 'Reason', key: 'reason', render: (v) => v ? <span style={{ whiteSpace: 'normal', wordBreak: 'break-word', display: 'block', minWidth: 120 }}>{v}</span> : '—' },
    { header: 'Status', key: 'status', render: (v, row) => <Badge status={v} byName={row.approved_by_name || row.pending_approver_name} /> },
    {
      header: 'Actions', key: 'id', align: 'right',
      render: (id, row) => row.status === 'pending' ? (
        <div style={{ display: 'flex', gap: '0.4rem', justifyContent: 'flex-end' }}>
          <Button size="sm" variant="success" onClick={() => handleApprove(id)}>Approve</Button>
          <Button size="sm" variant="danger" onClick={() => { setRejectModal(id); setRejectReason(''); }}>Reject</Button>
        </div>
      ) : null,
    },
  ];

  const inp = { width: '100%', padding: '0.6rem 0.875rem', border: `1.5px solid ${theme.border}`, borderRadius: '0.5rem', fontSize: '0.875rem', color: theme.text, outline: 'none', marginBottom: '0.75rem', boxSizing: 'border-box' };
  const lbl = { display: 'block', fontSize: '0.8rem', fontWeight: 600, color: theme.text, marginBottom: '0.35rem' };

  const tabList = ['My Leaves', ...(isManager ? ['Team Requests'] : [])];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
        <h1 style={{ fontSize: '1.5rem', fontWeight: 700, color: theme.text, margin: 0 }}>Leave Management</h1>
        <Button variant="primary" onClick={() => setApplyModal(true)}>+ Apply Leave</Button>
      </div>

      {/* Balances */}
      {balances.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: '0.875rem' }}>
          {balances.map((lb, i) => (
            <div key={i} style={{ background: theme.white, borderRadius: '0.75rem', padding: '1rem', border: `1px solid ${theme.border}`, borderTop: `3px solid ${[theme.primary, theme.info, theme.success, theme.warning, '#8B5CF6'][i % 5]}`, textAlign: 'center' }}>
              <p style={{ fontSize: '1.4rem', fontWeight: 700, color: theme.text, margin: 0 }}>{lb.balance ?? lb.remaining ?? '—'}</p>
              <p style={{ fontSize: '0.75rem', color: theme.textMuted, margin: '0.25rem 0 0' }}>{lb.leave_type_name || lb.leave_type?.name}</p>
              <p style={{ fontSize: '0.65rem', color: theme.textMuted, margin: '0.1rem 0 0' }}>days left</p>
            </div>
          ))}
        </div>
      )}

      <div style={{ display: 'flex', gap: '0.25rem', borderBottom: `2px solid ${theme.border}` }}>
        {tabList.map((t) => (
          <button key={t} onClick={() => setActiveTab(t)} style={{ padding: '0.625rem 1.25rem', background: 'none', border: 'none', cursor: 'pointer', fontWeight: activeTab === t ? 700 : 400, color: activeTab === t ? theme.primary : theme.textMuted, borderBottom: activeTab === t ? `2px solid ${theme.primary}` : '2px solid transparent', marginBottom: '-2px', fontSize: '0.875rem' }}>
            {t}
          </button>
        ))}
      </div>

      {activeTab === 'My Leaves' && (
        <Card padding="0">
          <Table columns={myCols} data={myRequests} loading={loading} emptyMessage="No leave requests found" />
        </Card>
      )}

      {activeTab === 'Team Requests' && isManager && (
        <Card padding="0">
          <Table columns={teamCols} data={teamRequests} loading={loading} emptyMessage="No team leave requests" />
        </Card>
      )}

      {/* Apply Modal */}
      <Modal isOpen={applyModal} onClose={closeApplyModal} title="Apply for Leave"
        footer={<><Button variant="outlineGray" onClick={closeApplyModal}>Cancel</Button><Button variant="primary" loading={saving} onClick={handleApply}>Submit Request</Button></>}>
        {applyError && (
          <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: '0.5rem', padding: '0.75rem 1rem', marginBottom: '1rem', color: '#991B1B', fontSize: '0.85rem', fontWeight: 500, lineHeight: 1.5 }}>
            ⚠️ {applyError}
          </div>
        )}
        <label style={lbl}>Leave Type *</label>
        <select style={inp} value={form.leave_type_id} onChange={(e) => setForm({ ...form, leave_type_id: e.target.value })}>
          <option value="">Select leave type</option>
          {leaveTypes.map((lt) => <option key={lt.id} value={lt.id}>{lt.name}</option>)}
        </select>

        {/* Half-day toggle */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.625rem', marginBottom: '0.75rem' }}>
          <input type="checkbox" id="half_day" checked={form.is_half_day} onChange={(e) => setForm({ ...form, is_half_day: e.target.checked })} style={{ width: '16px', height: '16px', cursor: 'pointer' }} />
          <label htmlFor="half_day" style={{ ...lbl, marginBottom: 0, cursor: 'pointer', fontWeight: 500 }}>Half Day</label>
        </div>

        {form.is_half_day ? (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            <div>
              <label style={lbl}>Date *</label>
              <input style={inp} type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} />
            </div>
            <div>
              <label style={lbl}>Period *</label>
              <select style={inp} value={form.half_day_period} onChange={(e) => setForm({ ...form, half_day_period: e.target.value })}>
                <option value="first_half">First Half (Morning)</option>
                <option value="second_half">Second Half (Afternoon)</option>
              </select>
            </div>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            <div><label style={lbl}>From Date *</label><input style={inp} type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} /></div>
            <div><label style={lbl}>To Date *</label><input style={inp} type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} /></div>
          </div>
        )}

        <label style={lbl}>Reason *</label>
        <textarea style={{ ...inp, resize: 'vertical', minHeight: '90px' }} value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} placeholder="Reason for leave..." />
      </Modal>

      <Modal isOpen={!!rejectModal} onClose={() => { setRejectModal(null); setRejectReason(''); }} title="Reject Leave Request"
        footer={<><Button variant="outlineGray" onClick={() => setRejectModal(null)}>Cancel</Button><Button variant="danger" loading={saving} onClick={handleReject}>Reject</Button></>}>
        <label style={lbl}>Reason *</label>
        <textarea style={{ ...inp, resize: 'vertical', minHeight: '90px' }} value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} placeholder="Enter rejection reason..." />
      </Modal>
    </div>
  );
}

export default MyLeave;
